// Clients API on the shared apiClient (base URL, auth, error semantics).
// Replaces the client-CRUD half of the retired services/api.ts ApiService.
import { deleteRequest, get, postJson, put } from './apiClient';

export interface Client {
  _id?: string; // backend may return _id
  id?: string; // frontend sometimes uses id
  name: string;
  email?: string;
  phoneCountryCode?: string; // e.g. +91
  phoneNumber?: string; // digits
  address?: string;
  labels?: string[]; // stored; UI can be added later
  /**
   * Suppliers this client would rather be quoted from, most-preferred first.
   * A preference, not a restriction: a line whose preferred supplier has no
   * good match for the item is still quoted from whoever does.
   */
  preferredVendors?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateClientData {
  name: string;
  email?: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
  address?: string;
  labels?: string[];
  preferredVendors?: string[];
}

export interface UpdateClientData {
  name?: string;
  email?: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
  address?: string;
  labels?: string[];
  preferredVendors?: string[];
}

export async function getClients(): Promise<Client[]> {
  const data = await get<any>('/clients');
  // Accept both { data: { clients: [] } } and { data: [] } shapes
  const list = data?.data?.clients ?? data?.data;
  if (!Array.isArray(list)) {
    // A contract break must not read as "no clients": say so, once, loudly.
    // eslint-disable-next-line no-console
    console.warn('[clientsApi] unexpected /clients response shape', data);
    return [];
  }
  return list;
}

/**
 * Create a client. On a validation refusal the thrown error carries
 * `isValidationError` + `validationErrors` (apiClient attaches them) so the
 * form can mark the offending fields.
 */
export async function createClient(payload: CreateClientData): Promise<Client> {
  const data = await postJson<any>('/clients', payload);
  return data?.data ?? data;
}

export async function updateClient(clientId: string, payload: UpdateClientData): Promise<Client> {
  const data = await put<any>(`/clients/${clientId}`, payload);
  return data?.data ?? data;
}

export async function deleteClient(clientId: string): Promise<void> {
  await deleteRequest(`/clients/${clientId}`);
}
